"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"
import { API_BASE_URL } from "../../../config/api"


// Inline SVG Icons for Premium UI
const RefreshIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
)

const DownloadIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
)

const FilterIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
)

const MagnifyingGlassIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
)

const ChatBubbleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a.75 0 0 1-1.074-.765 7.99 7.99 0 0 0 1.257-3.807C3.223 14.85 2 12.537 2 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
  </svg>
)

const QueueIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />
  </svg>
)

const ClockIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
)

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
)

const DocumentChartBarIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
)

function Queue() {
  const { getQueueStats, getAllRecipients, fetchAllUsers, user } = useAuth()
  const navigate = useNavigate()
  const allowedQueueRoles = ["superadmin", "admin", "support"]
  const hasQueueAccess = allowedQueueRoles.includes(user?.role)
  const [queueStats, setQueueStats] = useState({})
  const [history, setHistory] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 100, totalRecords: 0, totalPages: 1 })
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [selectedOperator, setSelectedOperator] = useState("all")
  const [selectedType, setSelectedType] = useState("all")
  const [selectedUser, setSelectedUser] = useState("all")
  const [users, setUsers] = useState([])
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0])
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingQty, setIsExportingQty] = useState(false)

  useEffect(() => {
    if (user && !hasQueueAccess) {
      navigate("/dashboard", { replace: true })
    }
  }, [user, hasQueueAccess, navigate])

  const loadQueueStats = async () => {
    try {
      const result = await getQueueStats()
      setQueueStats(result.queueStats || {})
    } catch (error) {
      console.error("Error loading queue stats:", error)
      setQueueStats({})
    }
  }

  const loadHistory = async (page = 1, limit = pagination.limit) => {
    setLoading(true)
    try {
      const result = await getAllRecipients({
        page,
        limit,
        status: statusFilter,
        userId: selectedUser,
        operator: selectedOperator,
        messageType: selectedType,
        startDate,
        endDate,
        search: searchTerm,
      })
      if (result.success) {
        setHistory(result.data)
        setPagination((prev) => ({ ...prev, page, limit, totalRecords: result.pagination.total || 0, totalPages: result.pagination.pages || 1 }))
      }
    } catch (error) {
      console.error("Error loading all recipients:", error)
      setHistory([])
    } finally {
      setLoading(false)
    }
  }

  const handleChangePageSize = (newLimit) => {
    setPagination((prev) => ({ ...prev, page: 1, limit: newLimit }))
    loadHistory(1, newLimit)
  }

  const handlePageChange = (newPage) => {
    loadHistory(newPage)
  }

  const refresh = async () => {
    setRefreshing(true)
    await Promise.all([loadQueueStats(), loadHistory(pagination.page, pagination.limit)])
    setRefreshing(false)
  }

  const applyFilters = async () => {
    await loadHistory(1, pagination.limit)
  }

  const exportFilteredData = async () => {
    setIsExporting(true)
    try {
      const token = localStorage.getItem("token") || ""
      const query = new URLSearchParams({
        status: statusFilter,
        userId: selectedUser,
        operator: selectedOperator,
        messageType: selectedType,
        startDate: startDate || "",
        endDate: endDate || "",
        search: searchTerm || "",
      }).toString()

      const response = await fetch(`${API_BASE_URL}/api/campaigns/queue/export-csv?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch CSV export")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `queue-export-${new Date().toISOString().slice(0, 10)}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Error exporting filtered data:", err)
      alert("Failed to export filtered queue data. Please try again.")
    } finally {
      setIsExporting(false)
    }
  }

  const exportQtyReport = async () => {
    setIsExportingQty(true)
    try {
      const token = localStorage.getItem("token") || ""
      const query = new URLSearchParams({
        status: statusFilter,
        userId: selectedUser,
        operator: selectedOperator,
        messageType: selectedType,
        startDate: startDate || "",
        endDate: endDate || "",
        search: searchTerm || "",
      }).toString()

      const response = await fetch(`${API_BASE_URL}/api/campaigns/queue/export-qty?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch Quantity Report")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `qty-report-${new Date().toISOString().slice(0, 10)}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Error exporting quantity report:", err)
      alert("Failed to export Quantity Report. Please try again.")
    } finally {
      setIsExportingQty(false)
    }
  }

  useEffect(() => {
    const loadFilterOptions = async () => {
      if (user?.role === "admin" || user?.role === "superadmin" || user?.role === "support") {
        const usersData = await fetchAllUsers()
        setUsers(usersData)
      }
    }

    loadFilterOptions()
    refresh()
  }, [user])

  const getUserNameById = (id) => {
    if (!id) return "-"
    const userObj = users.find((u) => String(u._id || u.id) === String(id))
    return userObj ? userObj.name || userObj.username || userObj.email || "Unknown" : "Unknown"
  }

  const getInitials = (userIdStr) => {
    if (!userIdStr) return "?"
    const userObj = users.find((u) => String(u._id || u.id) === String(userIdStr))
    const name = userObj ? userObj.name || userObj.username || userObj.email : ""
    if (!name) return "?"
    return name.substring(0, 2).toUpperCase()
  }

  const filteredHistory = history
  const totalMessages = pagination.totalRecords || 0

  if (!hasQueueAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-100 dark:from-gray-900 dark:to-gray-800">
        <div className="p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg text-center">
          <h1 className="text-2xl font-bold text-red-700 dark:text-red-300">Access Denied</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">You do not have permission to view queue messages.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <div>
      

        {/* Filter Configuration Card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-150 dark:border-gray-750 shadow-sm p-6">
          

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">Operator</label>
              <select
                value={selectedOperator}
                onChange={(e) => setSelectedOperator(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
              >
                <option value="all">All Operators</option>
                <option value="cloud">Cloud API</option>
                <option value="karix">Karix</option>
                <option value="pinacle">Pinacle</option>
              </select>
            </div>

            {users.length > 0 && (
              <div>
                <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">User Client</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
                >
                  <option value="all">All Users</option>
                  {users.map((u) => (
                    <option key={u._id || u.id} value={u._id || u.id}>
                      {u.name || u.username || u.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">Message Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
              >
                <option value="all">All Types</option>
                <option value="marketing">Marketing</option>
                <option value="utility">Utility</option>
                <option value="authentication">Authentication</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">From Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">To Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">Limit Per Page</label>
              <select
                value={pagination.limit}
                onChange={(e) => handleChangePageSize(Number(e.target.value))}
                className="w-full px-3.5 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
              >
                <option value={100}>100 records</option>
                <option value={200}>200 records</option>
                <option value={500}>500 records</option>
                <option value={1000}>1,000 records</option>
                <option value={5000}>5,000 records</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-gray-400 dark:text-gray-500 block mb-1">Search Recipient</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Number or details..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold"
                />
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
            {/* Stats Badges */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/30 rounded-xl text-xs font-bold shadow-sm">
                <QueueIcon className="w-3.5 h-3.5" />
                <span>Queued: <span className="font-extrabold">{queueStats.campaigns?.messageCount ?? 0}</span></span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-900/30 rounded-xl text-xs font-bold shadow-sm">
                <ChatBubbleIcon className="w-3.5 h-3.5" />
                <span>Total Logs: <span className="font-extrabold">{loading ? "..." : totalMessages}</span></span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-250 dark:border-emerald-900/30 rounded-xl text-xs font-bold shadow-sm">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span>Consumer: <span className="font-extrabold">Active</span></span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 self-end sm:self-auto">
 <div className="flex items-center gap-2 self-start md:self-auto">
            <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg shadow-sm">
              Last Sync: {new Date().toLocaleTimeString()}
            </span>
          </div>              
<button
                onClick={refresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-650 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-bold border border-gray-250 dark:border-gray-600 disabled:opacity-50 transition-all shadow-sm"
              >
                <RefreshIcon className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Syncing..." : "Sync Now"}
              </button>
              <button
                onClick={applyFilters}
                disabled={loading || isExporting}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Filtering...</span>
                  </>
                ) : (
                  "Apply Filters"
                )}
              </button>
              <button
                onClick={exportFilteredData}
                disabled={isExporting || isExportingQty || loading}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold shadow-sm transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <DownloadIcon className="w-4 h-4" />
                    <span>Export CSV</span>
                  </>
                )}
              </button>
              <button
                onClick={exportQtyReport}
                disabled={isExportingQty || isExporting || loading}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-sm transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isExportingQty ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Exporting Qty...</span>
                  </>
                ) : (
                  <>
                    <DocumentChartBarIcon className="w-4 h-4" />
                    <span>Export Qty Report</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Styled Table Card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-150 dark:border-gray-750 shadow-sm overflow-hidden flex flex-col">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200/50 dark:border-gray-700/50">
                <tr>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Campaign</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Template</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Recipient</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">User</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Status</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Type</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Credits</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Error Description</th>
                  <th className="py-4 px-6 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-xs">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-150 dark:divide-gray-750">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
                        <p className="font-semibold text-sm">Loading logs...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center justify-center p-6">
                        <ChatBubbleIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
                        <p className="font-semibold text-base mb-1">No logs found</p>
                        <p className="text-xs text-gray-400">Adjust filters or search parameters and try again.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((item) => (
                    <tr
                      key={item._id || item.id}
                      className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">{item.campaignName || "-"}</td>
                      <td className="py-4 px-6 font-mono text-xs text-gray-500 dark:text-gray-400">
                        {item.templateName ? (
                          <span className="bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-md">
                            {item.templateName}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="py-4 px-6 font-mono text-gray-900 dark:text-white font-medium">{item.number || "-"}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-blue-500 text-white rounded-lg flex items-center justify-center font-bold text-[10px]">
                            {getInitials(item.userId)}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white text-xs">
                            {getUserNameById(item.userId)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                            item.status === "delivered"
                              ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900/50"
                              : item.status === "sent"
                                ? "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/50"
                                : item.status === "read"
                                  ? "bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900/50"
                                  : item.status === "failed"
                                    ? "bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/50"
                                    : "bg-gray-50 text-gray-700 border border-gray-250 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-650"
                          }`}
                        >
                          {item.status || "Pending"}
                        </span>
                      </td>
                      <td className="py-4 px-6 capitalize">
                        <span className="text-gray-600 dark:text-gray-300 font-semibold text-xs">
                          {item.messageType || "marketing"}
                        </span>
                      </td>
                      <td className="py-4 px-6 font-semibold text-green-600 dark:text-green-400 text-xs">
                        ₹{(item.costPerMessage || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-red-500 dark:text-red-400 text-xs max-w-xs truncate">
                        {item.errorMessage || item.errorCode || "-"}
                      </td>
                      <td className="py-4 px-6 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN") : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {!loading && filteredHistory.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-150 dark:border-gray-750 gap-4 mt-auto">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing Page <span className="font-semibold text-gray-900 dark:text-white">{pagination.page}</span> of{" "}
                <span className="font-semibold text-gray-900 dark:text-white">{pagination.totalPages}</span> (
                <span className="font-semibold text-gray-900 dark:text-white">{pagination.totalRecords}</span> total records)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page <= 1}
                  className="px-3.5 py-1.5 border border-gray-300 dark:border-gray-650 bg-white dark:bg-gray-700 text-xs font-semibold rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.page + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3.5 py-1.5 border border-gray-300 dark:border-gray-650 bg-white dark:bg-gray-700 text-xs font-semibold rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export default Queue
